import type { RefinedTrolleyLineItems } from '~/stores/trolley.store';
import type { ActionItems, MonetateAction, RecTokenMap } from '~/types/monetate/monetate.type';

const categories = [
  'elektrisch-gereedschap',
  'schroeven-bevestigingsmiddelen',
  'elektra',
  'gereedschap-accessoires',
  'handgereedschap',
  'hang-sluitwerk',
  'kranen-douches',
  'ijzerwaren',
  'installatie',
  'lijmen-kitten-tapes',
  'reinigen-onderhoud',
  'tuingereedschap',
  'verf-accessoires',
  'verlichting',
  'verwarming-ventilatie',
  'werkplaats',
  'werkkleding-pbm',
];

/**
 * Extracts all recommendation tokens and their associated item IDs from actions.
 * @param data - Array of Monetate actions
 * @returns Array of objects containing item IDs and their recommendation tokens
 */
export function extractAllRecTokens(data: MonetateAction[]): RecTokenMap[] {
  return data.flatMap((item) =>
    item.items.map((subItem) => ({
      id: subItem.id,
      recToken: subItem.recToken,
    }))
  );
}

/**
 * Extracts item IDs from actions based on the component name.
 * @param data - Array of Monetate actions
 * @param componentName - Name of the component to filter by
 * @returns Array of item IDs
 */
export function extractItemsByComponent(data: MonetateAction[], componentName: string): string[] {
  return data
    .filter((item) => item.component === componentName)
    .flatMap((item) => item.items.map((subItem) => subItem.id));
}

/**
 * Extracts lines from a TrolleyLineItem and maps them to a standard format.
 * @param trolleyLine - The trolley line data to process
 * @returns Array of line items with product details
 */
export function extractLineFromTrolley(trolleyLine: RefinedTrolleyLineItems): Array<{
  pid: string;
  quantity: number;
  sku: string;
  value: string;
  currency: string;
}> {
  const lines: Array<{
    pid: string;
    quantity: number;
    sku: string;
    value: string;
    currency: string;
  }> = [];

  Object.keys(trolleyLine).forEach((channelKey) => {
    const channel = channelKey as keyof typeof trolleyLine;
    const lineItems = trolleyLine[channel];
    if (lineItems.length) {
      lineItems.forEach((line) => {
        lines.push({
          pid: line.product_code,
          quantity: line.quantity,
          sku: line.product_code,
          value: line.product?.prices?.raw.gross.toString() || '0',
          currency: 'EUR',
        });
      });
    }
  });

  return lines;
}

export function transformPurchaseLines(data) {
  const lines = data.map((item) => ({
    value: item.prices.product_raw.toFixed(2),
    sku: item.product.code,
    pid: item.product.code,
    quantity: item.item_quantity,
    currency: 'EUR',
  }));

  return lines;
}

export function FilterProducts(items) {
  return items.filter((item) => item.product.sellable);
}

export function isCategory(path: string) {
  return categories.some((category) => path.includes(category));
}

export function getMatchingCategory(path: string) {
  return categories.find((category) => path.includes(category)) || null;
}

export function getActionItems(component: string, actions: any): ActionItems | null {
  const action = actions?.find((item) => item.component === component);
  if (action?.items) {
    return {
      ids: action.items.map((item) => item.id),
      recTokens: action.items.map((item) => item.recToken),
    };
  }
  return null;
}
